/**
 * LearnMoreLink — small "↗" icon button that opens an external Microsoft
 * Learn (or other doc) URL in a new tab without triggering the surrounding
 * row's primary click target.
 *
 * Used inside PriorityAction rows where the row itself is a `<Link>` to an
 * in-app deep dive: the action's primary CTA stays on the in-app route, and
 * the user can click this icon as a separate affordance to open the doc in
 * a new tab. We render a `<button>` (not a nested `<a>`) because nested
 * anchors are invalid HTML, and use `stopPropagation` + `preventDefault` so
 * the surrounding Link doesn't navigate when the icon is clicked.
 */

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

interface LearnMoreLinkProps {
  /** Microsoft Learn (or equivalent) URL. When null/undefined the component
   *  renders nothing — callers can pass `action.learnMoreUrl` directly. */
  url: string | null | undefined
  /** Visual size of the icon. 'sm' suits compact rows (PillarShell Action),
   *  'md' suits the larger PriorityQueue rows. Defaults to 'sm'. */
  size?: 'sm' | 'md'
  /** Optional accessible label override. Defaults to "Open Microsoft Learn
   *  reference in a new tab" — adequate for the BPA rows that ship today. */
  ariaLabel?: string
  /** Optional className passthrough so callers can tune color contrast for
   *  light vs dark surfaces (the dashboard rows sit on dark-800; the
   *  Security Center rows on cream/paper). */
  className?: string
}

export default function LearnMoreLink({
  url,
  size = 'sm',
  ariaLabel = 'Open Microsoft Learn reference in a new tab',
  className,
}: LearnMoreLinkProps) {
  if (!url) return null

  const iconSize = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(url, '_blank', 'noopener,noreferrer')
      }}
      title="Open Microsoft Learn reference"
      aria-label={ariaLabel}
      className={
        className
        ?? 'inline-flex flex-none items-center justify-center rounded text-slate-500 transition-colors hover:text-slate-100'
      }
    >
      <ArrowTopRightOnSquareIcon className={iconSize} aria-hidden />
    </button>
  )
}
